import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell, Check, Ticket as TicketIcon, Lightbulb, FolderKanban, ListTodo, Repeat, CheckCircle2,
} from 'lucide-react'
import type { AssignmentNotification } from '@/lib/types'
import { formatDate } from '@/lib/utils'

/** Label + ikon per jenis notifikasi. Satu sumber supaya konsisten. */
const KIND: Record<AssignmentNotification['kind'], { label: string; Icon: typeof Bell }> = {
  ticket_assigned: { label: 'Tiket untuk kamu',    Icon: TicketIcon },
  ticket_accepted: { label: 'Tiketmu diterima',    Icon: CheckCircle2 },
  ticket_rejected: { label: 'Tiketmu ditolak',     Icon: TicketIcon },
  ticket_done:     { label: 'Tiket selesai',       Icon: CheckCircle2 },
  ticket_project:  { label: 'Project dari tiket',  Icon: FolderKanban },
  idea_routed:     { label: 'Ide untuk kamu',      Icon: Lightbulb },
  project_pic:     { label: 'PIC project',        Icon: FolderKanban },
  collaborator:    { label: 'Kolaborator project', Icon: FolderKanban },
  task:            { label: 'Task baru',           Icon: ListTodo },
  subtask:         { label: 'Subtask baru',        Icon: ListTodo },
  recurring:       { label: 'Task rutin',          Icon: Repeat },
}

/** Ke mana sebuah notifikasi membawa saat diklik. */
function destination(n: AssignmentNotification): string {
  if (n.kind.startsWith('ticket_') && n.kind !== 'ticket_project') return '/ticketing'
  if (n.kind === 'idea_routed') return '/ideas'
  if (n.projectId) return `/projects/${n.projectId}`
  return '/my-dashboard'
}

export default function NotificationsPage() {
  const navigate = useNavigate()
  const [items, setItems] = useState<AssignmentNotification[]>([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/notifications')
      if (!res.ok) return
      const d = await res.json()
      setItems(Array.isArray(d?.items) ? d.items : [])
      setUnread(Number(d?.unread ?? 0))
    } catch { /* abaikan */ } finally { setLoading(false) }
  }, [])

  useEffect(() => { void load() }, [load])

  const markAll = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })))
    setUnread(0)
    await fetch('/api/notifications', {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ all: true }),
    }).catch(() => {})
  }

  const open = async (n: AssignmentNotification) => {
    if (!n.read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)))
      setUnread((u) => Math.max(0, u - 1))
      await fetch('/api/notifications', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: n.id }),
      }).catch(() => {})
    }
    navigate(destination(n))
  }

  const grouped = useMemo(() => ({
    baru: items.filter((n) => !n.read),
    lama: items.filter((n) => n.read),
  }), [items])

  const Row = ({ n }: { n: AssignmentNotification }) => {
    const meta = KIND[n.kind] ?? { label: n.kind, Icon: Bell }
    const { Icon } = meta
    return (
      <button
        onClick={() => open(n)}
        className={`w-full flex items-start gap-3 px-4 py-3.5 text-left border-b border-[#F0EFEB] last:border-b-0 transition-colors hover:bg-[#F7F6F3] ${
          n.read ? 'bg-white' : 'bg-[#FDFCF9]'
        }`}
      >
        <span className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center ${
          n.read ? 'bg-[#F4F3F0] text-[#8A8A85]' : 'bg-[#1A1916] text-white'
        }`}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-body font-bold uppercase tracking-wider text-[#8A8A85]">{meta.label}</span>
            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626]" />}
          </span>
          <span className="block mt-0.5 text-sm font-body font-semibold text-[#1A1916] break-words">
            {n.title ?? n.projectName ?? 'Tanpa judul'}
          </span>
          <span className="block mt-0.5 text-xs font-body text-[#8A8A85]">
            {n.assignerName ? `dari ${n.assignerName}` : ''}
            {n.assignerDepartment ? ` · ${n.assignerDepartment}` : ''}
            {` · ${formatDate(n.createdAt)}`}
          </span>
        </span>
      </button>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-5 py-7 space-y-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#1A1916]">Notifikasi</h1>
          <p className="mt-1 text-sm font-body text-[#8A8A85]">
            {unread > 0 ? `${unread} belum dibaca` : 'Semua sudah dibaca'}
          </p>
        </div>
        {unread > 0 && (
          <button onClick={markAll}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-[#E8E7E3] text-xs font-body font-semibold text-[#3A3A36] hover:border-[#C8C7BF] transition-colors">
            <Check className="w-3.5 h-3.5" /> Tandai semua dibaca
          </button>
        )}
      </div>

      {loading ? (
        <div className="bg-white border border-[#E8E7E3] rounded-2xl p-10 text-center text-sm font-body text-[#A8A8A2]">
          Memuat notifikasi...
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E8E7E3] rounded-2xl p-12 text-center">
          <Bell className="w-7 h-7 text-[#C8C7BF] mx-auto" />
          <p className="mt-3 text-sm font-body font-semibold text-[#1A1916]">Belum ada notifikasi</p>
          <p className="mt-1 text-xs font-body text-[#A8A8A2]">
            Tiket, ide, dan penugasan project yang ditujukan ke kamu akan muncul di sini.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.baru.length > 0 && (
            <section>
              <h2 className="mb-2 px-1 text-[11px] font-body font-bold uppercase tracking-wider text-[#8A8A85]">Belum dibaca</h2>
              <div className="bg-white border border-[#E8E7E3] rounded-2xl overflow-hidden">
                {grouped.baru.map((n) => <Row key={n.id} n={n} />)}
              </div>
            </section>
          )}
          {grouped.lama.length > 0 && (
            <section>
              <h2 className="mb-2 px-1 text-[11px] font-body font-bold uppercase tracking-wider text-[#8A8A85]">Sebelumnya</h2>
              <div className="bg-white border border-[#E8E7E3] rounded-2xl overflow-hidden">
                {grouped.lama.map((n) => <Row key={n.id} n={n} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}
